import moment from 'moment-timezone';
import { DateObject } from 'react-multi-date-picker';
import {
  assetDetailsUrls,
  eventPageType,
  CALENDAR_TYPE,
  EVENT_TYPE,
  EVENT_PAGE_PORTFOLIO_TRENDS,
  EVENT_PAGE_PORTFOLIO_SITE_LIST,
  allEquipCategory,
  EVENT_VISIBILITY,
} from '../constants';
import { getEnvSegment } from './navigation';
import {
  valueFilterOptions,
  workOrderOptions,
} from '../views/containers/SiteListMap/components/utils';

/** Same path shape as EventShareDialog for calendar / HTML descriptions. */
export function buildEquipmentInsightsCalendarUrl(
  subscriptionCode,
  assetId,
  groupId,
) {
  if (!subscriptionCode || !assetId) return null;
  const equipmentPath = `/${encodeURIComponent(
    subscriptionCode,
  )}/${encodeURIComponent(assetId)}/equipment-details/pai/${
    assetDetailsUrls.details
  }${groupId ? `?grpId=${groupId}` : ''}`;
  return `${window.location.origin}${getEnvSegment()}/insights${equipmentPath}`;
}

export const getAllEventDates = events => {
  const result = [];

  events.forEach(ev => {
    result.push(ev.eventFrom * 1000);
  });

  return result;
};

export const convertUnixToDateFormat = events =>
  events.map(ev => moment(ev.eventFrom * 1000).format('YYYY-MM-DD'));

export const getSumOfEvents = events => {
  const now = new Date();
  const allDates = getAllEventDates(events);

  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  );

  const endOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() + 1,
  );

  return allDates.filter(ev => {
    const eventDate = new Date(ev);
    return (
      eventDate >= now && eventDate >= startOfToday && eventDate < endOfToday
    );
  }).length;
};

export const getPastEventDetails = events => {
  const today = new Date();

  const startOfPreviousMonth = new Date(
    today.getFullYear(),
    today.getMonth() - 1,
    1,
  );

  const startOfNextMonth = new Date(
    today.getFullYear(),
    today.getMonth() + 1,
    1,
  );

  return events
    .filter(ev => {
      const eventDate = new Date(ev.eventFrom * 1000);

      return (
        eventDate >= startOfPreviousMonth &&
        eventDate < startOfNextMonth &&
        eventDate < today
      );
    })
    .sort((a, b) => b.eventTo - a.eventTo);
};

export const getUpcomingEventDetails = events => {
  const now = Date.now();

  return events
    .filter(ev => {
      const eventTime = ev.eventFrom * 1000;
      return eventTime >= now;
    })
    .sort((a, b) => a.eventFrom - b.eventFrom);
};

export const convertUnixToDateTimeFormat = unixValue =>
  moment.unix(unixValue).format('MMM D-YYYY, hh:mm A');

export const convertUnixToDate = unixValue =>
  moment.unix(unixValue).format('YY-MM-DD');

export const constructSubject = (eventType, pageType, location) =>
  `${eventType} for ${pageType} at ${location}`;

export const getFromToTimes = () => {
  const now = new DateObject();
  const { minute } = now;

  const from = new DateObject(now.valueOf());
  from.set('second', 0).set('millisecond', 0);

  if (minute < 30) {
    from.set('minute', 30);
  } else {
    from.add(1, 'hour').set('minute', 0);
  }

  const to = new DateObject(from.valueOf());
  to.add(1, 'hour');

  return { from, to };
};

export const getDateBlocks = (start, end, maxDays) => {
  const result = [];
  const s = new Date(start);
  while (s <= end) {
    const e = new Date(s.getFullYear(), s.getMonth(), s.getDate() + maxDays);
    result.push({ start: new Date(s), end: e <= end ? e : new Date(end) });
    s.setDate(s.getDate() + maxDays + 1);
  }
  return result;
};

export function resolveCalendarProvider(calendarType) {
  if (!calendarType) return '';

  const value =
    typeof calendarType === 'string' ? calendarType : calendarType?.calendar;

  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

export function isOutlookCalendarProvider(calendarType) {
  return resolveCalendarProvider(calendarType) === CALENDAR_TYPE.OUTLOOK;
}

export function isGoogleCalendarProvider(calendarType) {
  return resolveCalendarProvider(calendarType) === CALENDAR_TYPE.GOOGLE;
}

/* eslint-disable indent */
export function generateCalendarLink({
  provider,
  subject,
  from,
  to,
  description,
}) {
  const resolvedProvider = resolveCalendarProvider(provider);
  const startTimeISO = new Date(from * 1000).toISOString();
  const endTimeISO = new Date(to * 1000).toISOString();

  const startTimeGoogle = new Date(from * 1000)
    .toISOString()
    .replace(/[-:]|\.\d{3}/g, '');
  const endTimeGoogle = new Date(to * 1000)
    .toISOString()
    .replace(/[-:]|\.\d{3}/g, '');
  if (resolvedProvider === CALENDAR_TYPE.GOOGLE) {
    const url = new URL('https://calendar.google.com/calendar/render');
    url.searchParams.set('action', 'TEMPLATE');
    url.searchParams.set('text', subject);
    url.searchParams.set('details', description);
    url.searchParams.set('dates', `${startTimeGoogle}/${endTimeGoogle}`);
    return url.toString();
  }
  const url = new URL('https://outlook.office.com/calendar/0/deeplink/compose');
  url.searchParams.set('subject', subject);
  url.searchParams.set('body', description);
  url.searchParams.set('startdt', startTimeISO);
  url.searchParams.set('enddt', endTimeISO);
  return url.toString();
}

export function generateEventDescription({
  eventDate,
  siteName,
  siteURL,
  orgName,
  equipmentList = [],
  exportedBy,
  visibility,
  subscriptionCode,
  groupId,
  equipmentUrlLabel = 'Equipment URL',
}) {
  const formattedDate = moment(eventDate).format('DD-MM-YYYY');

  const equipmentLines =
    equipmentList && equipmentList?.length > 0
      ? equipmentList
          .map((eq, index) => {
            const activities =
              eq?.activities?.length > 0
                ? eq.activities
                    .map(a => `&nbsp;&nbsp;&nbsp;&nbsp;- ${a.name}`)
                    .join('<br/>')
                : '&nbsp;&nbsp;&nbsp;&nbsp;- None';

            const partsToCarry =
              eq?.partsToCarry?.length > 0
                ? eq.partsToCarry
                    .map(p => `&nbsp;&nbsp;&nbsp;&nbsp;- ${p.partName}`)
                    .join('<br/>')
                : '&nbsp;&nbsp;&nbsp;&nbsp;- None';

            const sub =
              eq?.subscriptionCode != null && eq?.subscriptionCode !== ''
                ? eq.subscriptionCode
                : subscriptionCode;
            const grp =
              eq?.groupId != null && eq?.groupId !== '' ? eq.groupId : groupId;
            const equipmentUrl = buildEquipmentInsightsCalendarUrl(
              sub,
              eq?.assetId,
              grp,
            );
            const equipmentUrlBlock =
              equipmentUrl && equipmentUrlLabel
                ? `<b>${equipmentUrlLabel}:</b> <a href="${equipmentUrl}">${equipmentUrl}</a><br/>`
                : '';

            return `
<b>Equipment-${index + 1}:</b> ${eq.assetName}<br/>
<b>Activity:</b><br/>
${activities}<br/>
<b>Parts to carry:</b><br/>
${partsToCarry}
${equipmentUrlBlock}`;
          })
          .join('<br/><br/>')
      : '';

  let visibilityText = '';

  if (visibility === EVENT_VISIBILITY.PUBLIC) {
    visibilityText = 'Public event';
  } else if (visibility === EVENT_VISIBILITY.ORG) {
    visibilityText = 'Event shared with organization';
  }

  let htmlDescription = `
<b>Event created from Abound calendar</b><br/>
${visibilityText ? `<b>${visibilityText}</b><br/>` : ''}
<b>Date:</b> ${formattedDate}<br/>
`;

  if (siteName) {
    htmlDescription += `<b>Site Name:</b> ${siteName}<br/>`;
  }

  if (orgName) {
    htmlDescription += `<b>Organization Name:</b> ${orgName}<br/>`;
  }

  if (siteURL) {
    htmlDescription += `<b>Site URL:</b> <a href="${siteURL}">${siteURL}</a><br/><br/>`;
  }

  if (equipmentLines) {
    htmlDescription += `${equipmentLines}<br/><br/>`;
  }

  htmlDescription += `<b>Exported by:</b> ${exportedBy}`;

  return htmlDescription;
}
/* eslint-enable indent */

export const FILTER_TYPE = {
  bySiteUuid: 'siteUuid',
  byBundleId: 'bundleId',
  byTrendViewBundleId: 'trendViewBundleId',
  global: 'global',
};

export const filterByServiceBundleId = (events, value) => {
  const { svcBundleId, groupdId } = value;
  const filteredEvents = events.filter(event => {
    if (event?.bundle) {
      const parsedData = JSON.parse(event?.bundle);
      return (
        parsedData?.serviceBundleId === svcBundleId &&
        parsedData?.pageType === EVENT_PAGE_PORTFOLIO_TRENDS &&
        parsedData?.groupId === groupdId
      );
    }
    return false;
  });
  return filteredEvents;
};

export const filterByServiceBundleIdAndAssetCat = (events, objValue) => {
  const { bundleId, assetCat, groupId } = objValue;
  const filteredEvents = events.filter(event => {
    if (event?.bundle) {
      const parsedData = JSON.parse(event?.bundle);
      return (
        parsedData?.serviceBundleId === bundleId &&
        parsedData?.equipCategoryId === assetCat &&
        parsedData?.pageType === EVENT_PAGE_PORTFOLIO_SITE_LIST &&
        parsedData?.groupId === groupId
      );
    }
    return false;
  });
  return filteredEvents;
};

export const commonFilter = (events, groupId) => {
  const filteredEvents = events.filter(event => {
    if (event?.bundle) {
      const parsedData = JSON.parse(event?.bundle);
      return parsedData?.groupId === groupId;
    }
    return false;
  });
  return filteredEvents;
};

export const getFilteredEvents = (events, type, value) => {
  let filteredEvents = events;

  if (!type) {
    return filteredEvents;
  }

  switch (type) {
    case FILTER_TYPE.bySiteUuid: {
      const { siteuuid, currentUserId } = value;
      filteredEvents = events.filter(
        event => event.siteUuid === siteuuid && event.userId === currentUserId,
      );
      break;
    }
    case FILTER_TYPE.byBundleId:
      filteredEvents = filterByServiceBundleIdAndAssetCat(events, value);
      break;
    case FILTER_TYPE.byTrendViewBundleId:
      filteredEvents = filterByServiceBundleId(events, value);
      break;
    default:
      filteredEvents = events;
  }

  return filteredEvents;
};

export const constructSubjectForSiteList = (
  eventType,
  isMapView,
  groupName,
  serviceBundleName,
  equipmentCategory,
  insightsFilter,
  pLabel,
  workOrder,
) => {
  const parts = [];

  if (eventType === EVENT_TYPE.PLAN) {
    parts.push(
      `Plan for work allocation from ${
        isMapView !== 'no' ? 'map view site list' : 'site list'
      }`,
    );
  }

  if (eventType === EVENT_TYPE.REVIEW) {
    parts.push(
      `Review for ${isMapView !== 'no' ? 'map view site list' : 'site list'}`,
    );
  }

  if (groupName) parts.push(groupName);
  if (serviceBundleName) parts.push(serviceBundleName);
  if (equipmentCategory) parts.push(equipmentCategory);
  if (insightsFilter) parts.push(insightsFilter);
  if (pLabel) parts.push(pLabel);
  if (workOrder) parts.push(workOrder);

  return parts.join(' | ');
};

export const constructSubjectForSiteTrendsView = (
  eventType,
  groupName,
  serviceBundleName,
  equipmentCategory,
  comboCodeDescription,
  filterLabel,
  isMap,
  siteListFilter,
  detailBtnIsActive = false,
) => {
  const parts = [];

  if (eventType === EVENT_TYPE.PLAN) {
    parts.push(
      `Plan for Work Allocation from Portfolio Trend ${
        detailBtnIsActive ? ` ${isMap ? 'Map View' : 'List View'}` : ''
      } report (${filterLabel})`,
    );
  }
  if (eventType === EVENT_TYPE.REVIEW) {
    parts.push(
      `Review for Work Allocation from Portfolio Trend ${
        detailBtnIsActive ? ` ${isMap ? 'Map View' : 'List View'}` : ''
      } report (${filterLabel})`,
    );
  }

  if (groupName) parts.push(groupName);

  if (serviceBundleName && !comboCodeDescription && filterLabel) {
    parts.push(serviceBundleName);
  }

  if (serviceBundleName && comboCodeDescription) {
    parts.push(serviceBundleName);
  }

  if (comboCodeDescription) {
    parts.push(comboCodeDescription);
  } else if (equipmentCategory) {
    parts.push(equipmentCategory);
  } else {
    parts.push('All equipment');
  }

  if (siteListFilter) {
    parts.push(siteListFilter);
  }

  return parts.join(' | ');
};

export const mapMobileToPercentKey = mobileValue => {
  const match = valueFilterOptions.find(
    opt => opt.mobileSupportedValue === mobileValue,
  );
  return match ? match.code : mobileValue;
};

export const mapMobileToWorkOrderId = mobileValue => {
  const match = workOrderOptions.find(
    opt => opt.mobileSupportedValue === mobileValue,
  );
  return match ? match.value : mobileValue;
};

export const buildPortfolioSiteListUrl = (bundleData = {}) => {
  const params = new URLSearchParams();

  if (
    bundleData.equipCategoryId &&
    bundleData.equipCategoryId !== allEquipCategory
  ) {
    params.set('eqc', bundleData.equipCategoryId);
  }

  if (bundleData?.portfolioSiteFilter?.insightStatus) {
    params.set('sts', bundleData?.portfolioSiteFilter?.insightStatus);
  }

  if (
    bundleData?.portfolioSiteFilter?.isMapView !== undefined &&
    bundleData.portfolioSiteFilter.isMapView !== null
  ) {
    params.set('map', bundleData.portfolioSiteFilter.isMapView ? 'yes' : 'no');
  }

  if (bundleData?.portfolioSiteFilter?.percentKey) {
    params.set(
      'ptype',
      mapMobileToPercentKey(bundleData.portfolioSiteFilter.percentKey),
    );
  }

  if (bundleData?.portfolioSiteFilter?.percentValue) {
    params.set('pvalue', bundleData.portfolioSiteFilter.percentValue);
  }

  if (bundleData?.portfolioSiteFilter?.workOrderId) {
    params.set(
      'wotype',
      mapMobileToWorkOrderId(bundleData.portfolioSiteFilter.workOrderId),
    );
  }

  return `/${bundleData?.serviceBundleId}/site-list?grpId=${
    bundleData?.groupId
  }&${params.toString()}`;
};

export const buildPortfolioSiteTrendsUrl = (bundleData = {}) => {
  const params = new URLSearchParams();

  const { portfolioTrendsFilter, equipCategoryId, groupId, serviceBundleId } =
    bundleData;
  const comboCode = portfolioTrendsFilter?.comboCode;

  if (portfolioTrendsFilter?.periodFilter) {
    params.set('viewBy', portfolioTrendsFilter.periodFilter);
  }

  if (comboCode?.id) {
    params.set('comboCode', comboCode.id);
  }

  if (comboCode?.status) {
    params.set('comboStatus', comboCode.status);
  }

  if (groupId) {
    params.set('grpId', groupId);
  }

  if (typeof portfolioTrendsFilter.isMap === 'boolean') {
    params.set('isMap', String(portfolioTrendsFilter.isMap));
  }

  const siteListFilter = portfolioTrendsFilter?.siteListFilter;
  if (siteListFilter) {
    if (siteListFilter.percentKey) {
      params.set('percentKey', siteListFilter.percentKey);
    }
    if (siteListFilter.percentValue) {
      params.set('percentValue', siteListFilter.percentValue);
    }
    if (siteListFilter.workOrderId) {
      params.set('workOrderId', siteListFilter.workOrderId);
    }
    if (siteListFilter.activityId) {
      params.set('activityId', siteListFilter.activityId);
    }
  }

  if (equipCategoryId && !comboCode?.id) {
    return `/${serviceBundleId}/trends-view/equipment/${equipCategoryId}?${params.toString()}`;
  }

  if (equipCategoryId && comboCode?.id) {
    return `/${serviceBundleId}/trends-view/opportunity/${equipCategoryId}?${params.toString()}`;
  }

  return `/${serviceBundleId}/trends-view?${params.toString()}`;
};

export const constructUrlForSite = (
  pageType,
  siteId,
  contractCode,
  eventId,
  bundle,
) => {
  const encodedSiteId = encodeURIComponent(siteId);
  const encodedContractCode = encodeURIComponent(contractCode);
  const encodedEventId = encodeURIComponent(eventId);
  const bundleData = bundle && JSON.parse(bundle);

  if (!bundle && (!siteId || !contractCode)) {
    return '/';
  }

  switch (pageType) {
    case eventPageType.siteSummary:
      return `/${encodedSiteId}/${encodedContractCode}/site-details/ais/active?eventId=${encodedEventId}&grpId=${bundleData?.groupId}`;
    case eventPageType.todoList:
      return `/${encodedSiteId}/${encodedContractCode}/site-details/pai/active?eventId=${encodedEventId}&grpId=${bundleData?.groupId}`;
    case eventPageType.liveConnect:
      return `/${encodedSiteId}/${encodedContractCode}/live-connect?eventId=${encodedEventId}&grpId=${bundleData?.groupId}`;
    case eventPageType.portfolioSiteList:
      return buildPortfolioSiteListUrl(bundleData);
    case eventPageType.portfolioSiteTrends:
      return buildPortfolioSiteTrendsUrl(bundleData);
    default:
      return '/';
  }
};

export const mapPercentKeyToMobile = key => {
  const match = valueFilterOptions.find(opt => opt.code === key);
  return match ? match.mobileSupportedValue : key;
};

export const mapWorkOrderIdToMobile = id => {
  const match = workOrderOptions.find(opt => opt.value === id);
  return match ? match.mobileSupportedValue : id;
};

export const mapWorkOrderIdToDesktop = id => {
  const match = workOrderOptions.find(opt => opt.mobileSupportedValue === id);
  return match ? match.value : id;
};

export const mapPercentKeyToDesktop = key => {
  const match = valueFilterOptions.find(
    opt => opt.mobileSupportedValue === key,
  );
  return match ? match.code : key;
};

export function roundToNextHalfHour(date) {
  const d = new Date(date);
  d.setSeconds(0, 0);
  if (d.getMinutes() < 30) {
    d.setMinutes(30, 0, 0);
  } else {
    d.setHours(d.getHours() + 1, 0, 0, 0);
  }
  return d;
}

export const getFormattedProcessedDate = (timestamp, timezone) => {
  if (timestamp === 0) {
    return '-';
  }
  const momentObj = timezone
    ? moment.tz(timestamp, timezone)
    : moment(timestamp);
  const formattedDate = momentObj?.format('DD MMM, YYYY');
  const abbr = momentObj?.format('z');
  const timezoneName = timezone?.includes('/')
    ? timezone?.split('/').pop()
    : timezone;

  const numericOffsetRegex = /^([+-])(\d{1,2})(:?)(\d{0,2})$/;
  const match = abbr?.match(numericOffsetRegex);

  if (!match) return `${formattedDate}, (${abbr}) ${timezoneName}`;

  const [, sign, hoursRaw, , minutesRaw] = match;
  const hours = hoursRaw.padStart(2, '0');
  const minutes = minutesRaw.padEnd(2, '0');

  return `${formattedDate}, (UTC${sign}${hours}:${minutes}) ${timezoneName}`;
};

export const getEventLabel = (t, noOfEventsToday) => {
  if (noOfEventsToday > 1) {
    return t('events_today', { count: noOfEventsToday });
  }
  if (noOfEventsToday === 1) {
    return t('event_today', { count: noOfEventsToday });
  }
  return t('no_events_today');
};
